import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  TrendingUp, TrendingDown, Trophy, Target, 
  Loader2, BarChart3, Calendar, Users, Minus
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Simple Bar Chart Component (no external library needed)
const SimpleBarChart = ({ data, maxValue, label, color = 'bg-primary' }) => (
  <div className="space-y-2">
    {data.map((item, index) => (
      <div key={index} className="flex items-center gap-3">
        <div className="w-24 text-sm truncate">{item.label}</div>
        <div className="flex-1 h-6 bg-muted rounded-full overflow-hidden">
          <div 
            className={`h-full ${color} rounded-full transition-all duration-500`}
            style={{ width: `${(item.value / maxValue) * 100}%` }}
          />
        </div>
        <div className="w-12 text-sm font-medium text-right">{item.value}</div>
      </div>
    ))}
  </div>
);

// Trend Indicator Component
const TrendIndicator = ({ current, previous, suffix = '' }) => {
  if (previous === 0 || previous === null) return <Minus className="h-4 w-4 text-muted-foreground" />;
  
  const diff = current - previous;
  const percentage = ((diff / previous) * 100).toFixed(1);
  
  if (diff > 0) {
    return (
      <span className="flex items-center text-green-600 text-sm">
        <TrendingUp className="h-4 w-4 mr-1" />
        +{percentage}%{suffix}
      </span>
    );
  } else if (diff < 0) {
    return (
      <span className="flex items-center text-red-600 text-sm">
        <TrendingDown className="h-4 w-4 mr-1" />
        {percentage}%{suffix}
      </span>
    );
  }
  return <Minus className="h-4 w-4 text-muted-foreground" />;
};

// Team Performance Card
const TeamPerformanceCard = ({ team }) => {
  const winRate = team.wins + team.losses > 0 
    ? ((team.wins / (team.wins + team.losses)) * 100).toFixed(1)
    : 0;
  
  const pointDiff = (team.points_scored || 0) - (team.points_allowed || 0);
  
  return (
    <Card data-testid={`team-perf-${team.id}`}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">{team.name}</CardTitle>
          <Badge variant={team.wins > team.losses ? 'default' : 'secondary'}>
            {team.wins}-{team.losses}
          </Badge>
        </div>
        <CardDescription>{team.age_category} • {team.gender}</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-sm text-muted-foreground">Win Rate</p>
            <p className="text-2xl font-bold">{winRate}%</p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Point Diff</p>
            <p className={`text-2xl font-bold ${pointDiff >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {pointDiff >= 0 ? '+' : ''}{pointDiff}
            </p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Points Scored</p>
            <p className="text-xl font-semibold">{team.points_scored || 0}</p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Points Allowed</p>
            <p className="text-xl font-semibold">{team.points_allowed || 0}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Main Performance Charts Page
export const PerformanceCharts = () => {
  const { teamId } = useParams();
  const [teams, setTeams] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(teamId || '');
  const [teamData, setTeamData] = useState(null);
  const [tournaments, setTournaments] = useState([]);
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (selectedTeam) {
      fetchTeamData(selectedTeam);
    }
  }, [selectedTeam]);

  const fetchInitialData = async () => {
    try {
      const [teamsRes, tournamentsRes] = await Promise.all([
        axios.get(`${API}/teams`),
        axios.get(`${API}/tournaments`)
      ]);
      
      setTeams(teamsRes.data);
      setTournaments(tournamentsRes.data);
      
      if (teamId) {
        setSelectedTeam(teamId);
      } else if (teamsRes.data.length > 0) {
        setSelectedTeam(teamsRes.data[0].id);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load performance data');
    } finally {
      setLoading(false);
    }
  };

  const fetchTeamData = async (id) => {
    try {
      const [teamRes, gamesRes] = await Promise.all([
        axios.get(`${API}/teams/${id}`),
        axios.get(`${API}/games`)
      ]);
      
      setTeamData(teamRes.data);
      
      // Filter games involving this team
      const teamGames = gamesRes.data.filter(
        g => g.team1_id === id || g.team2_id === id
      );
      setGames(teamGames);
    } catch (error) {
      console.error('Error fetching team data:', error);
    }
  };

  // Calculate performance metrics
  const calculateMetrics = () => {
    if (!teamData || games.length === 0) return null;

    const completedGames = games.filter(g => g.status === 'completed');
    
    let totalScored = 0;
    let totalAllowed = 0;
    let wins = 0;
    let losses = 0;
    let highestScore = 0;
    let lowestScore = Infinity;
    let biggestWin = 0;
    let gameResults = [];

    completedGames.forEach(game => {
      const isTeam1 = game.team1_id === selectedTeam;
      const teamScore = isTeam1 ? game.team1_score : game.team2_score;
      const oppScore = isTeam1 ? game.team2_score : game.team1_score;
      const oppName = isTeam1 ? game.team2_name : game.team1_name;
      
      totalScored += teamScore;
      totalAllowed += oppScore;
      
      if (teamScore > oppScore) {
        wins++;
        const margin = teamScore - oppScore;
        if (margin > biggestWin) biggestWin = margin;
      } else if (oppScore > teamScore) {
        losses++;
      }
      
      if (teamScore > highestScore) highestScore = teamScore;
      if (teamScore < lowestScore) lowestScore = teamScore;
      
      gameResults.push({
        opponent: oppName,
        score: teamScore,
        oppScore: oppScore,
        result: teamScore > oppScore ? 'W' : teamScore < oppScore ? 'L' : 'T',
        margin: teamScore - oppScore
      });
    });

    const avgScored = completedGames.length > 0 ? (totalScored / completedGames.length).toFixed(1) : 0;
    const avgAllowed = completedGames.length > 0 ? (totalAllowed / completedGames.length).toFixed(1) : 0;

    return {
      gamesPlayed: completedGames.length,
      wins,
      losses,
      winRate: completedGames.length > 0 ? ((wins / completedGames.length) * 100).toFixed(1) : 0,
      totalScored,
      totalAllowed,
      avgScored,
      avgAllowed,
      pointDiff: totalScored - totalAllowed,
      highestScore,
      lowestScore: lowestScore === Infinity ? 0 : lowestScore,
      biggestWin,
      gameResults
    };
  };

  const metrics = calculateMetrics();

  // Leaderboard data
  const teamLeaderboard = teams
    .filter(t => t.wins + t.losses > 0)
    .map(t => ({
      label: t.name,
      value: t.wins,
      winRate: ((t.wins / (t.wins + t.losses)) * 100).toFixed(1)
    }))
    .sort((a, b) => b.value - a.value)
    .slice(0, 10);

  const scoringLeaderboard = teams
    .filter(t => t.points_scored > 0)
    .map(t => ({
      label: t.name,
      value: t.points_scored || 0
    }))
    .sort((a, b) => b.value - a.value)
    .slice(0, 10);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <BarChart3 className="h-8 w-8" />
            Performance Charts
          </h1>
          <p className="text-muted-foreground">
            Historical performance data and statistics
          </p>
        </div>
        
        <div className="w-64">
          <Select value={selectedTeam} onValueChange={setSelectedTeam}>
            <SelectTrigger>
              <SelectValue placeholder="Select a team" />
            </SelectTrigger>
            <SelectContent>
              {teams.map(team => (
                <SelectItem key={team.id} value={team.id}>
                  {team.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <Tabs defaultValue="team" className="w-full">
        <TabsList className="mb-6">
          <TabsTrigger value="team" className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            Team Stats
          </TabsTrigger>
          <TabsTrigger value="leaderboards" className="flex items-center gap-2">
            <Trophy className="h-4 w-4" />
            Leaderboards
          </TabsTrigger>
          <TabsTrigger value="history" className="flex items-center gap-2">
            <Calendar className="h-4 w-4" />
            Game History
          </TabsTrigger>
        </TabsList>

        {/* Team Stats Tab */}
        <TabsContent value="team">
          {teamData && metrics ? (
            <div className="space-y-6">
              {/* Key Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardDescription>Record</CardDescription>
                    <CardTitle className="text-3xl">
                      {metrics.wins}-{metrics.losses}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Badge variant={parseFloat(metrics.winRate) >= 50 ? 'default' : 'secondary'}>
                      {metrics.winRate}% Win Rate
                    </Badge>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardDescription>Avg Points/Game</CardDescription>
                    <CardTitle className="text-3xl">{metrics.avgScored}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground">
                      {metrics.avgAllowed} allowed
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardDescription>Point Differential</CardDescription>
                    <CardTitle className={`text-3xl ${metrics.pointDiff >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {metrics.pointDiff >= 0 ? '+' : ''}{metrics.pointDiff}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground">
                      Total: {metrics.totalScored} - {metrics.totalAllowed}
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardDescription>Biggest Win</CardDescription>
                    <CardTitle className="text-3xl text-green-600">
                      +{metrics.biggestWin}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground">
                      High: {metrics.highestScore} pts
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Scoring Breakdown */}
              <Card>
                <CardHeader>
                  <CardTitle>Scoring by Game</CardTitle>
                  <CardDescription>Points scored in each game</CardDescription>
                </CardHeader>
                <CardContent>
                  <SimpleBarChart
                    data={metrics.gameResults.map((g, i) => ({
                      label: `vs ${g.opponent.substring(0, 10)}...`,
                      value: g.score
                    }))}
                    maxValue={Math.max(...metrics.gameResults.map(g => g.score), 1)}
                    color="bg-primary"
                  />
                </CardContent>
              </Card>
            </div>
          ) : (
            <Card>
              <CardContent className="py-12 text-center">
                <Target className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
                <h3 className="font-semibold mb-2">No Performance Data</h3>
                <p className="text-muted-foreground">
                  {selectedTeam ? 'This team has no completed games yet.' : 'Select a team to view performance data.'}
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Leaderboards Tab */}
        <TabsContent value="leaderboards">
          <div className="grid md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Trophy className="h-5 w-5 text-yellow-500" />
                  Most Wins
                </CardTitle>
              </CardHeader>
              <CardContent>
                {teamLeaderboard.length > 0 ? (
                  <div className="space-y-3">
                    {teamLeaderboard.map((team, index) => (
                      <div key={index} className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                          index === 0 ? 'bg-yellow-500 text-white' :
                          index === 1 ? 'bg-gray-400 text-white' :
                          index === 2 ? 'bg-amber-700 text-white' :
                          'bg-muted'
                        }`}>
                          {index + 1}
                        </div>
                        <div className="flex-1">
                          <p className="font-medium">{team.label}</p>
                          <p className="text-sm text-muted-foreground">{team.winRate}% win rate</p>
                        </div>
                        <div className="font-bold text-lg">{team.value}</div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground py-8">No data available</p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target className="h-5 w-5 text-blue-500" />
                  Top Scoring Teams
                </CardTitle>
              </CardHeader>
              <CardContent>
                {scoringLeaderboard.length > 0 ? (
                  <SimpleBarChart
                    data={scoringLeaderboard}
                    maxValue={Math.max(...scoringLeaderboard.map(t => t.value), 1)}
                    color="bg-blue-500"
                  />
                ) : (
                  <p className="text-center text-muted-foreground py-8">No data available</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* All Teams Performance */}
          <Card className="mt-6">
            <CardHeader>
              <CardTitle>All Teams Performance</CardTitle>
              <CardDescription>Overview of all teams with game history</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {teams
                  .filter(t => t.wins + t.losses > 0)
                  .sort((a, b) => b.wins - a.wins)
                  .map(team => (
                    <TeamPerformanceCard key={team.id} team={team} />
                  ))}
              </div>
              {teams.filter(t => t.wins + t.losses > 0).length === 0 && (
                <p className="text-center text-muted-foreground py-8">
                  No teams have completed games yet.
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Game History Tab */}
        <TabsContent value="history">
          {metrics && metrics.gameResults.length > 0 ? (
            <Card>
              <CardHeader>
                <CardTitle>Game History</CardTitle>
                <CardDescription>All completed games for {teamData?.name}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {metrics.gameResults.map((game, index) => (
                    <div 
                      key={index}
                      className={`flex items-center justify-between p-4 rounded-lg border ${
                        game.result === 'W' ? 'bg-green-50 border-green-200 dark:bg-green-900/20' :
                        game.result === 'L' ? 'bg-red-50 border-red-200 dark:bg-red-900/20' :
                        'bg-muted'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <Badge variant={game.result === 'W' ? 'default' : game.result === 'L' ? 'destructive' : 'secondary'}>
                          {game.result}
                        </Badge>
                        <div>
                          <p className="font-medium">vs {game.opponent}</p>
                          <p className="text-sm text-muted-foreground">
                            {game.margin > 0 ? `Won by ${game.margin}` : game.margin < 0 ? `Lost by ${Math.abs(game.margin)}` : 'Tie'}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold">
                          {game.score} - {game.oppScore}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="py-12 text-center">
                <Calendar className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
                <h3 className="font-semibold mb-2">No Game History</h3>
                <p className="text-muted-foreground">
                  Complete some games to see the history here.
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};
